/**
 * Application shell for the Landing portal.
 *
 * Wraps the portal in I18nProvider, ThemeProvider, and BrowserRouter
 * following the standard IPA provider nesting order (Architecture Section F1).
 * The landing page is a single-route portal that serves as the platform
 * entry point for unauthenticated visitors.
 *
 * @module landing/App
 */

import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '../../shared/src/theme/ThemeProvider';
import { I18nProvider } from '../../shared/src/i18n/I18nProvider';
import { SeoMeta } from '../../shared/src/seo/SeoMeta';
import { LandingNav } from './components/LandingNav';
import { LandingPage } from './LandingPage';
import '../../shared/src/design-system/design-system.css';
import '../../shared/src/responsive/responsive.css';
import '../../shared/src/responsive/breakpoints.css';
import './landing-page.css';

/**
 * Landing page layout with navigation, SEO meta, and assembled sections.
 */
function LandingLayout(): React.JSX.Element {
  const { t } = useTranslation('landing');

  return (
    <>
      <SeoMeta
        title={t('meta.title')}
        description={t('meta.description')}
      />
      <LandingNav />
      <LandingPage />
    </>
  );
}

/**
 * Root application component for the Landing portal.
 *
 * Provider nesting order: I18nProvider > ThemeProvider > BrowserRouter
 */
export function App(): React.JSX.Element {
  return (
    <I18nProvider>
      <ThemeProvider>
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <Routes>
            <Route path="*" element={<LandingLayout />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </I18nProvider>
  );
}
